import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, CheckCircle, XCircle, Loader2, Clock, Download, Trash2, Tag, Globe, ChevronDown, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const API = '/api';

function StatusBadge({ status }) {
  const map = {
    pending: { cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: Clock, label: 'Pending' },
    processing: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2, label: 'Searching...' },
    completed: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Done' },
    failed: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, label: 'Failed' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs font-medium border ${s.cls}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {s.label}
    </Badge>
  );
}

function KeywordSection({ keyword, domains }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-zinc-100">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-50 text-left transition-colors">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />}
        <Tag className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-sm font-medium text-zinc-700">{keyword}</span>
        <Badge variant="outline" className="ml-auto text-xs border-zinc-200">{domains.length}</Badge>
      </button>
      {open && domains.length > 0 && (
        <div className="pl-10 pr-4 pb-2 space-y-0.5">
          {domains.map(d => (
            <div key={d} className="flex items-center gap-2 py-0.5">
              <Globe className="w-3 h-3 text-zinc-400" />
              <span className="font-mono text-xs text-zinc-600">{d}</span>
            </div>
          ))}
        </div>
      )}
      {open && domains.length === 0 && (
        <p className="pl-10 pr-4 pb-2 text-xs text-zinc-400">No domains found</p>
      )}
    </div>
  );
}

export default function KeywordSearchPage() {
  const [keywordsText, setKeywordsText] = useState('');
  const [searches, setSearches] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSearchId, setSelectedSearchId] = useState(null);
  const [viewMode, setViewMode] = useState('keywords');
  const [detail, setDetail] = useState(null);

  const fetchSearches = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/keyword-searches`, { withCredentials: true });
      setSearches(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!selectedSearchId) { setDetail(null); return; }
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        const { data } = await axios.get(`${API}/keyword-search/${selectedSearchId}`, { withCredentials: true });
        if (!cancelled) setDetail(data);
      } catch (err) { console.error(err); }
    };
    fetchDetail();
    const interval = setInterval(fetchDetail, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedSearchId]);

  useEffect(() => {
    fetchSearches();
    const iv = setInterval(fetchSearches, 10000);
    return () => clearInterval(iv);
  }, [fetchSearches]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const keywords = keywordsText.split('\n').map(k => k.trim()).filter(Boolean);
    if (keywords.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/keyword-search`, { keywords }, { withCredentials: true });
      setKeywordsText('');
      toast.success(`Searching ${keywords.length} keyword(s)`);
      setSelectedSearchId(data.id);
      fetchSearches();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to start search');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/keyword-search/${id}`, { withCredentials: true });
      if (selectedSearchId === id) setSelectedSearchId(null);
      toast.success('Deleted');
      fetchSearches();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownload = (search) => {
    if (!search?.all_domains?.length) return;
    const text = search.all_domains.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unique_domains.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="keyword-search-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-1 space-y-5">
            <div className="border border-zinc-200 bg-white p-5" data-testid="keyword-input-section">
              <h2 className="text-lg font-bold tracking-tight font-heading text-zinc-950 mb-1">
                <Search className="w-5 h-5 inline mr-2 -mt-0.5" />Keyword Search
              </h2>
              <p className="text-sm text-zinc-500 font-body mb-3">Find archived domains by keywords in Wayback Machine</p>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="keyword-search-form">
                <textarea
                  value={keywordsText}
                  onChange={(e) => setKeywordsText(e.target.value)}
                  placeholder={"crypto exchange\nonline store\ntech startup"}
                  className="w-full h-36 font-mono text-sm border border-zinc-300 rounded-sm p-3 focus:ring-2 focus:ring-blue-600 focus:outline-none resize-y bg-white"
                  data-testid="keyword-input"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400">{keywordsText.split('\n').filter(k => k.trim()).length} keyword(s)</span>
                  <Button type="submit" disabled={submitting || !keywordsText.trim()} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-5 h-9" data-testid="search-keywords-button">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching...</> : <><Search className="w-4 h-4 mr-2" /> Search</>}
                  </Button>
                </div>
              </form>
            </div>

            {/* History */}
            <div className="border border-zinc-200 bg-white" data-testid="keyword-searches-history">
              <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">History</span>
              </div>
              <ScrollArea className="max-h-[400px]">
                {searches.length === 0 ? (
                  <p className="p-4 text-xs text-zinc-400 font-body">No searches yet</p>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {searches.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSelectedSearchId(s.id); setViewMode('keywords'); }}
                        role="button"
                        tabIndex={0}
                        className={`w-full text-left p-3 hover:bg-zinc-50 transition-colors cursor-pointer ${selectedSearchId === s.id ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}
                        data-testid={`search-item-${s.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <StatusBadge status={s.status} />
                          <div className="flex gap-1">
                            {s.status === 'completed' && s.total_domains > 0 && (
                              <span onClick={(e) => { e.stopPropagation(); setSelectedSearchId(s.id); }} role="button" tabIndex={0} className="text-zinc-400 hover:text-blue-600 cursor-pointer" data-testid={`download-search-${s.id}`}><Download className="w-3.5 h-3.5" /></span>
                            )}
                            <span onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }} role="button" tabIndex={0} className="text-zinc-400 hover:text-rose-600 cursor-pointer" data-testid={`delete-search-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></span>
                          </div>
                        </div>
                        <div className="text-xs text-zinc-600">
                          {s.total_keywords || 0} keywords {s.status === 'completed' && <span className="text-blue-600 font-medium ml-1">{s.total_domains || 0} domains</span>}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">{s.created_at ? new Date(s.created_at).toLocaleString() : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>

          {/* Right column — Results */}
          <div className="lg:col-span-2 border border-zinc-200 bg-white" data-testid="keyword-results">
            <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Results</span>
              {detail?.status === 'completed' && (
                <>
                  <div className="ml-auto flex gap-1">
                    <button
                      onClick={() => setViewMode('keywords')}
                      className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${viewMode === 'keywords' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                      data-testid="view-by-keywords"
                    >By Keywords</button>
                    <button
                      onClick={() => setViewMode('domains')}
                      className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${viewMode === 'domains' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                      data-testid="view-all-domains"
                    >All Domains ({detail.total_domains})</button>
                  </div>
                </>
              )}
            </div>

            {!detail ? (
              <div className="flex flex-col items-center justify-center h-64 text-zinc-400">
                <Search className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm font-body">Enter keywords and click Search</p>
              </div>
            ) : detail.status === 'processing' ? (
              <div className="p-8 space-y-4">
                <div className="flex items-center gap-3">
                  <Progress value={detail.progress} className="h-2 flex-1" />
                  <span className="text-sm font-mono text-zinc-500">{detail.progress}%</span>
                </div>
                <p className="text-sm text-zinc-500 font-body text-center">Searching Wayback Machine... ({detail.downloaded_files || 0}/{detail.total_keywords || '?'} keywords)</p>
              </div>
            ) : detail.status === 'failed' ? (
              <div className="p-6 text-sm text-rose-600">{detail.error || 'Search failed'}</div>
            ) : detail.status === 'completed' ? (
              <ScrollArea className="h-[calc(100vh-280px)]">
                {viewMode === 'keywords' && detail.results ? (
                  <div data-testid="keyword-results-list">
                    {detail.results.map(r => (
                      <KeywordSection key={r.keyword} keyword={r.keyword} domains={r.domains} />
                    ))}
                  </div>
                ) : viewMode === 'domains' && detail.all_domains ? (
                  <div className="p-4" data-testid="all-domains-list">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-zinc-700">{detail.all_domains.length} unique domains</span>
                      {detail.all_domains.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => handleDownload(detail)} className="text-xs gap-1" data-testid="download-all-domains">
                          <Download className="w-3.5 h-3.5" /> Download .txt
                        </Button>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {detail.all_domains.map(d => (
                        <div key={d} className="flex items-center gap-2 py-1 px-2 hover:bg-zinc-50 rounded-sm">
                          <Globe className="w-3.5 h-3.5 text-zinc-400" />
                          <span className="font-mono text-xs text-zinc-700">{d}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </ScrollArea>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
